import Link from 'next/link';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { useSnapshot } from 'valtio/react';

import { userManager } from '@paid-ui/models/user';

const linkVariants = cva(
  'relative flex cursor-pointer appearance-none flex-col items-center justify-center gap-2 whitespace-nowrap rounded-[10px] py-2.5 text-xs text-white/70 no-underline transition-colors duration-150 ease-linear hover:text-white focus:text-white',
  {
    variants: {
      active: {
        true: 'bg-[#002B66] font-medium text-white',
      },
      impersonation: {
        true: '',
      },
    },
    compoundVariants: [
      {
        active: true,
        impersonation: true,
        className: 'bg-purple-dark',
      },
    ],
  },
);

const mobileLinkVariants = cva(
  'relative flex cursor-pointer appearance-none items-center gap-x-3 rounded px-3 py-2.5 font-semibold text-white no-underline transition-colors duration-150 ease-linear',
  {
    variants: {
      active: {
        true: 'bg-blue hover:bg-blue-dark active:bg-blue-dark',
      },
    },
  },
);

interface NavbarLinkProps extends Omit<React.ComponentPropsWithoutRef<typeof Link>, 'href'> {
  href?: string;
  isMobile?: boolean;
  isActive?: boolean;
}

export const NavbarLink: React.FC<NavbarLinkProps> = ({
  href,
  isActive = false,
  isMobile = false,
  hidden,
  className,
  children,
  ...restProps
}) => {
  const { impersonation } = useSnapshot(userManager);

  if (hidden || !href) {
    return null;
  }

  if (isMobile) {
    return (
      <div>
        <Link
          href={href}
          className={twMerge(mobileLinkVariants({ active: isActive, className }))}
          {...restProps}
        >
          {children}
        </Link>
      </div>
    );
  }

  return (
    <div className="p-1.5">
      <Link
        href={href}
        className={twMerge(linkVariants({ active: isActive, impersonation, className }))}
        {...restProps}
      >
        {children}
      </Link>
    </div>
  );
};
